import { BlockStatus } from '@next-space/fe-api-idl';
import { throttle } from 'lodash-es';
import { useCallback } from 'react';
import { DESTROY_BLOCK, LIST_REMOVE_BLOCK } from 'src/redux/actions';
import { blocksSlice } from 'src/redux/reducers/blocks';
import { dispatch, getState } from 'src/redux/store';
import type { NextBlock } from 'src/redux/types';
import { updatePageMetas } from 'src/services/page-metas';
import { isPageLike } from 'src/utils/block-type-utils';
import { checkPageId } from '../page/check-page-id';

const dirtyBlocks = new Set<string>();

const deletePageLocally = (uuid: string) => {
  const { blocks } = getState();
  const block = blocks[uuid];
  if (!block) return;
  const loop = (subId: string) => {
    const block = blocks[subId];
    if (!block) return;

    if (subId !== uuid && isPageLike(block.type)) {
      updatePageMetas({ [subId]: { tag: 'CACHE_EXPIRED', timestamp: Date.now() } });
    }
    // NOTE: 有甲乙两端， A、B 页面，B是A的子页面。乙删除B页面，甲彻底删除A页面。
    // 这种情况下，乙被同步的时候，A的subNodes里已经没有B了。但是B还得被打上`CACHE_EXPIRED`的标记。
    for (const blockId of Object.keys(blocks)) {
      const block = blocks[blockId];
      if (block == null) continue;
      if (block.status === BlockStatus.DELETED && block.parentId === subId) {
        updatePageMetas({ [blockId]: { tag: 'CACHE_EXPIRED', timestamp: Date.now() } });
      }
    }

    dispatch(DESTROY_BLOCK({ uuid: subId }));
    blocks[subId]?.subNodes.forEach(loop);
  };
  loop(uuid);
};

const updateBlocks = throttle(
  async () => {
    const blocks = [...dirtyBlocks];
    dirtyBlocks.clear();

    const blockRecords = await Promise.all(
      blocks.map(async (uuid) => {
        const res = await checkPageId(uuid);
        if (res.code === 200) {
          updatePageMetas({ [uuid]: undefined });
          return res.data;
        }
        if (res.code === 3005) {
          // 如果页面找不到(3005)
          const blockType = getState().blocks[uuid]?.type;
          deletePageLocally(uuid);
          if (isPageLike(blockType)) {
            updatePageMetas({ [uuid]: { tag: 'NOT_FOUND' } });
          }
        }
        if ([1406, 1405].includes(res.code)) {
          updatePageMetas({ [uuid]: { tag: 'NEED_PASSWORD' } });
        }
        if (res.code === 1403) {
          // 无访问权限
          const blockType = getState().blocks[uuid]?.type;
          deletePageLocally(uuid);
          if (isPageLike(blockType)) {
            updatePageMetas({ [uuid]: { tag: 'NO_PERMISSION' } });
          }
        }
      })
    );

    const updatedBlocks: { [id: string]: NextBlock } = {};
    for (const blockRecord of blockRecords) {
      if (!blockRecord) continue;
      updatedBlocks[blockRecord.uuid] = blockRecord as NextBlock;
    }
    dispatch(blocksSlice.actions.update({ blocks: updatedBlocks }));

    for (const blockRecord of blockRecords) {
      if (!blockRecord) continue;
      if (blockRecord.status === BlockStatus.DELETED) {
        dispatch(
          LIST_REMOVE_BLOCK({
            ignoreOp: true,
            uuid: blockRecord.uuid,
          })
        );
      }
    }
  },
  40,
  { leading: false }
);

export function useScheduleSyncBlock() {
  return useCallback((uuid: string) => {
    dirtyBlocks.add(uuid);
    void updateBlocks();
  }, []);
}
